import React from "react";
import { NavigateFunction } from "react-router-dom";
import { ThemeProvider } from "@emotion/react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Checkbox from "@mui/material/Checkbox";
import { theme } from "../AddSite/StepsStepper";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { Alert, DialogActions } from "@mui/material";
import { handleSubmitNewGroupUser } from "./GroupUsersUtils/handleSubmitNewGroupUser";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { useAppStore } from "../../../appStore";
import { logger } from "../../../utils/logger";
import { VisibleScreensSelect } from "./VisibleScreensSelect";
import {
  ALARM_REVIEW_ACCESS_LEVEL,
  ScreenOption,
  VIEW_ONLY_ACCESS_LEVEL,
  VISIBLE_SCREENS_FIELD,
  describeVocabularyError,
  fetchScreenVocabulary,
  isViewOnly,
  readVisibleScreens,
  withVisibleScreensForLevel,
} from "../../utils/viewOnlyAccess";

export const AddGroupUserStepTwo = ({
  newUserInfo,
  setNewUserInfo,
  setAddGroupUserScreenOpen,
  setUserNameString,
  setAlertState,
  groupUsersSearchString,
  activeData,
  setActiveData,
  setErrorStateMessage,
  setSubmitErrorState,
  setActiveStep,
  navigate,
}: {
  newUserInfo: any;
  setNewUserInfo: any;
  setAddGroupUserScreenOpen: Dispatcher<boolean>;
  setUserNameString: Dispatcher<string>;
  setAlertState: any;
  groupUsersSearchString: any;
  activeData: any;
  setActiveData: any;
  setErrorStateMessage: any;
  setSubmitErrorState: any;
  setActiveStep: Dispatcher<number>;
  navigate: NavigateFunction;
}) => {
  const [stepTwoInfo, setStepTwoInfo] = React.useState({
    ...newUserInfo,
    send_cognito_invitation: newUserInfo.send_cognito_invitation,
    create_user: newUserInfo.create_user,
    clips: newUserInfo.clips,
    view_passwords: newUserInfo.view_passwords,
    alert_muting: newUserInfo.alert_muting,
    live_streaming: newUserInfo.live_streaming,
    create_wireguard_tunnels: newUserInfo.create_wireguard_tunnels,
  });

  // ENG-1815: same View Only screen picker as AboutUserDialog, so a View Only
  // user can be scoped at creation. The user does not exist yet, so there is
  // no per-user CHM signal here; Health is offered and the backend validates
  // it on create (ENG-1814).
  const { userPermissions } = useAppStore();
  const canManageUsers = Boolean(userPermissions?.manage_users);
  const [screenVocabulary, setScreenVocabulary] = React.useState<
    ScreenOption[]
  >([]);
  const [vocabularyError, setVocabularyError] = React.useState<string | null>(
    null
  );

  React.useEffect(() => {
    if (!canManageUsers) return;
    let cancelled = false;
    fetchScreenVocabulary().then(
      (options) => {
        if (!cancelled) {
          setScreenVocabulary(options);
          setVocabularyError(null);
        }
      },
      (reason) => {
        logger.warn("Could not load the visible-screens vocabulary:", reason);
        if (!cancelled) setVocabularyError(describeVocabularyError(reason));
      }
    );
    return () => {
      cancelled = true;
    };
  }, [canManageUsers]);

  const selectAccessLevel = (level: number) => {
    setStepTwoInfo((previousState: any) => {
      return { ...previousState, access_level: level };
    });
  };

  // Rendered under the selected read-only radio (View Only or Alarm Review).
  const screenPicker =
    canManageUsers && isViewOnly(stepTwoInfo.access_level) ? (
      <div className="pl-[32px] pb-[8px]">
        <VisibleScreensSelect
          value={readVisibleScreens(stepTwoInfo)}
          onChange={(next) => {
            setStepTwoInfo((previousState: any) => ({
              ...previousState,
              [VISIBLE_SCREENS_FIELD]: next,
            }));
          }}
          options={screenVocabulary}
          loadError={vocabularyError}
        />
      </div>
    ) : null;

  return (
    <div>
      <div className="absolute top-[160px] left-[30px] w-[540px]">
        <ThemeProvider theme={theme}>
          <div>
            <FormControl variant="standard">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={stepTwoInfo.send_cognito_invitation}
                      onChange={(event, checked) => {
                        setNewUserInfo((previousState: any) => {
                          return {
                            ...previousState,
                            send_cognito_invitation: checked,
                          };
                        });
                        setStepTwoInfo((previousState: any) => {
                          return {
                            ...previousState,
                            send_cognito_invitation: checked,
                          };
                        });
                      }}
                    />
                  }
                  label="Send Cognito Invitation"
                  sx={{ color: "#001943" }}
                />
              </FormGroup>
              <FormHelperText>
                Send new user an email to create their password.
              </FormHelperText>
            </FormControl>
          </div>
          <br></br>
          <div>
            <p className="font-mulish text-[18px]  text-actuate-blue">
              Access Level
            </p>
            <div className="pl-[10px] py-[10px]">
              <FormControl>
                <RadioGroup value={stepTwoInfo.access_level}>
                  <FormControlLabel
                    onClick={() => selectAccessLevel(VIEW_ONLY_ACCESS_LEVEL)}
                    value={VIEW_ONLY_ACCESS_LEVEL}
                    control={<Radio />}
                    label="View Only"
                    sx={{ color: "#001943" }}
                  />
                  {/* The screen picker sits directly under whichever read-only
                      radio is selected. */}
                  {Number(stepTwoInfo.access_level) ===
                    VIEW_ONLY_ACCESS_LEVEL && screenPicker}
                  {/* ENG-2010: View Only plus setting alert outcomes. admin puts
                      these users in the View Only group too, so the screen
                      picker applies to both read-only levels. */}
                  <FormControlLabel
                    onClick={() => selectAccessLevel(ALARM_REVIEW_ACCESS_LEVEL)}
                    value={ALARM_REVIEW_ACCESS_LEVEL}
                    control={<Radio />}
                    label="Alarm Review (View Only + set alert outcomes)"
                    sx={{ color: "#001943" }}
                  />
                  {Number(stepTwoInfo.access_level) ===
                    ALARM_REVIEW_ACCESS_LEVEL && screenPicker}
                  <FormControlLabel
                    onClick={() => selectAccessLevel(1)}
                    value={1}
                    control={<Radio />}
                    label="Arm and Disarm Only (Lowest)"
                    sx={{ color: "#001943" }}
                  />

                  <FormControlLabel
                    onClick={() => selectAccessLevel(2)}
                    value={2}
                    control={<Radio />}
                    label="Manage Existing Cameras"
                    sx={{ color: "#001943" }}
                  />
                  <FormControlLabel
                    onClick={() => selectAccessLevel(3)}
                    value={3}
                    control={<Radio />}
                    label="Self-Service (can add new cameras)"
                    sx={{ color: "#001943" }}
                  />
                  <FormControlLabel
                    onClick={() => selectAccessLevel(4)}
                    value={4}
                    control={<Radio />}
                    label="Self-Service with Delete Permissions (Highest)"
                    sx={{ color: "#001943" }}
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
          <p className="font-mulish text-[18px]  text-actuate-blue">
            Permissions
          </p>
          <div className="pl-[11px] ">
            <FormControl variant="standard">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={stepTwoInfo.create_user}
                      onChange={(event, checked) => {
                        setNewUserInfo((previousState: any) => {
                          return {
                            ...previousState,
                            create_user: checked,
                          };
                        });
                        setStepTwoInfo((previousState: any) => {
                          return {
                            ...previousState,
                            create_user: checked,
                          };
                        });
                      }}
                    />
                  }
                  label="Create User"
                  sx={{ color: "#001943" }}
                />
              </FormGroup>
              <FormHelperText>
                User can create other users under their group
              </FormHelperText>
            </FormControl>
          </div>
          <div className="pl-[11px]">
            <FormControl variant="standard">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={stepTwoInfo.clips}
                      onChange={(event, checked) => {
                        setNewUserInfo((previousState: any) => {
                          return {
                            ...previousState,
                            clips: checked,
                          };
                        });
                        setStepTwoInfo((previousState: any) => {
                          return {
                            ...previousState,
                            clips: checked,
                          };
                        });
                      }}
                    />
                  }
                  label="Clips / AI Link"
                  sx={{ color: "#001943" }}
                />
              </FormGroup>
              <FormHelperText>User can view Clips / AI Link</FormHelperText>
            </FormControl>
          </div>
          <div className="pl-[11px]">
            <FormControl variant="standard">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={stepTwoInfo.view_passwords}
                      onChange={(event, checked) => {
                        setNewUserInfo((previousState: any) => {
                          return {
                            ...previousState,
                            view_passwords: checked,
                          };
                        });
                        setStepTwoInfo((previousState: any) => {
                          return {
                            ...previousState,
                            view_passwords: checked,
                          };
                        });
                      }}
                    />
                  }
                  label="View Passwords"
                  sx={{ color: "#001943" }}
                />
              </FormGroup>
              <FormHelperText>
                User can view passwords. We recommend this be enabled if this
                user will be tasked with camera onboarding.{" "}
              </FormHelperText>
            </FormControl>
          </div>
          <div className="pl-[11px]">
            <FormControl variant="standard">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={stepTwoInfo.alert_muting}
                      onChange={(event, checked) => {
                        setNewUserInfo((previousState: any) => {
                          return {
                            ...previousState,
                            alert_muting: checked,
                          };
                        });
                        setStepTwoInfo((previousState: any) => {
                          return {
                            ...previousState,
                            alert_muting: checked,
                          };
                        });
                      }}
                    />
                  }
                  label="View Alert Muting"
                  sx={{ color: "#001943" }}
                />
              </FormGroup>
              <FormHelperText>
                User can mute alerts from their triage on the Alerts page.
              </FormHelperText>
            </FormControl>
          </div>
          <div className="pl-[11px]">
            <FormControl variant="standard">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={stepTwoInfo.live_streaming}
                      onChange={(event, checked) => {
                        setNewUserInfo((previousState: any) => {
                          return {
                            ...previousState,
                            live_streaming: checked,
                          };
                        });
                        setStepTwoInfo((previousState: any) => {
                          return {
                            ...previousState,
                            live_streaming: checked,
                          };
                        });
                      }}
                    />
                  }
                  label="Live Streaming"
                  sx={{ color: "#001943" }}
                />
              </FormGroup>
              <FormHelperText>
                User can view live camera streams from the Alerts page.
              </FormHelperText>
            </FormControl>
          </div>
          <div className="pl-[11px]">
            <FormControl variant="standard">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={stepTwoInfo.create_wireguard_tunnels}
                      onChange={(event, checked) => {
                        setNewUserInfo((previousState: any) => {
                          return {
                            ...previousState,
                            create_wireguard_tunnels: checked,
                          };
                        });
                        setStepTwoInfo((previousState: any) => {
                          return {
                            ...previousState,
                            create_wireguard_tunnels: checked,
                          };
                        });
                      }}
                    />
                  }
                  label="Create WireGuard Tunnels"
                  sx={{ color: "#001943" }}
                />
              </FormGroup>
              <FormHelperText>
                User can create and manage WireGuard tunnels.
              </FormHelperText>
            </FormControl>
          </div>
          <br></br>
        </ThemeProvider>
        <DialogActions>
          <div>
            <OrangeTextButton
              text="Submit"
              disabled={false}
              onClick={() => {
                setActiveStep(3);
                handleSubmitNewGroupUser(
                  // The screen list is only posted for a View Only user.
                  withVisibleScreensForLevel(
                    stepTwoInfo,
                    stepTwoInfo.access_level
                  ),
                  setAddGroupUserScreenOpen,
                  setUserNameString,
                  setAlertState,
                  groupUsersSearchString,
                  activeData,
                  setActiveData,
                  setNewUserInfo,
                  setErrorStateMessage,
                  setSubmitErrorState,
                  setActiveStep,
                  navigate
                );
              }}
            />
          </div>
        </DialogActions>
      </div>
    </div>
  );
};
